import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import {
	getDaysInMonthGrid,
	getDaysInWeekGrid,
	formatLocalDate,
	getProfessionalHomeCombinedSessionsDateRange,
	parseLocalDateYmd,
	startOfLocalDay,
	endOfLocalDay,
	filterSessionsByHistoryDateRange,
} from './utils/calendarLogic';
import { useProfessionalCalendar } from '@/context/ProfessionalCalendarContext';
import { CalendarMonthView } from './components/CalendarMonthView';
import { CalendarWeekView } from './components/CalendarWeekView';
import { ProfessionalSessionModal } from './components/ProfessionalSessionModal';
import { ProfessionalProntuarioModal } from './components/ProfessionalProntuarioModal';
import { ProfessionalTutorialsModal } from './components/ProfessionalTutorialsModal';
import { ProfessionalHomeSummaryFooter } from './components/ProfessionalHomeSummaryFooter';
import { ProfessionalHomeGreetingNotification } from './components/ProfessionalHomeGreetingNotification';
import { ProfessionalHistoryPanel } from './components/ProfessionalHistoryPanel';
import { parseProfessionalTutorialHomeReturnState } from '@/constants/professionalTutorialDemo';
import {
	getProfessionalHomeOpenSession,
	getProfessionalHomeOpenProntuario,
} from './utils/professionalHomeNavigation';
import type { OpenProntuarioNavigationState } from './utils/professionalHomeNavigation';
import { isProfessionalHomeGreetingDismissedForPageLoad } from './utils/professionalHomeGreetingDismissed';
import { PROFESSIONAL_HOME_SESSIONS_REFETCH_INTERVAL_MS } from './utils/professionalHomeSessionsRefetch';
import { PROFESSIONAL_BOOKING_STEP_DELAY_MS } from '@/pages/professional/sessions/utils/professionalBookingFlow';
import { professionalHasP2PPermission } from '@/utils/professionalPermissionTags';
import { waitMs } from '@/utils/waitMs';
import { encodeUrl } from '@/utils/encodeUrl';
import { useGetSessions, useGetSession } from '@/services';

type ProfessionalLayoutContext = {
	data: Professionals.Professional | undefined;
	isLoading: boolean;
};

const ProfessionalHome = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { data: professional } =
		useOutletContext<ProfessionalLayoutContext>();
	const canSchedulePatientSessions =
		professionalHasP2PPermission(professional);
	const {
		currentDate,
		setCurrentDate,
		view,
		now,
		weekScrollAlignKey,
		bumpWeekScrollAlignKey,
		historyStatusFilter,
		historyDateRange,
	} = useProfessionalCalendar();
	const [openedSession, setOpenedSession] = useState<Sessions.Session | null>(
		null,
	);
	const [isOpenedSessionModalVisible, setIsOpenedSessionModalVisible] =
		useState(false);
	const [openProntuarioConfig, setOpenProntuarioConfig] =
		useState<OpenProntuarioNavigationState | null>(null);
	const [isTutorialModalOpen, setIsTutorialModalOpen] = useState(false);
	const [tutorialModalInitialId, setTutorialModalInitialId] = useState<
		string | null
	>(null);

	// --- LÓGICA DE DATAS E GRID ---
	const daysGrid =
		view === 'MONTH'
			? getDaysInMonthGrid(currentDate)
			: getDaysInWeekGrid(currentDate);

	const sessionsDateRange = useMemo(
		() =>
			getProfessionalHomeCombinedSessionsDateRange(
				currentDate,
				view,
				now,
			),
		[currentDate, view, formatLocalDate(now)],
	);

	// --- BUSCA DE DADOS ---
	const {
		data: calendarSessionsData,
		isFetching,
		isLoading: isCalendarSessionsLoading,
	} = useGetSessions(
		{
			isMe: true,
			limit: 100,
			date_a: sessionsDateRange.dateA.toISOString(),
			date_b: sessionsDateRange.dateB.toISOString(),
		},
		{
			refetchInterval: PROFESSIONAL_HOME_SESSIONS_REFETCH_INTERVAL_MS,
		},
	);

	const historySessionsQueryParams = useMemo(() => {
		const params: Sessions.SessionParams & { isMe?: boolean } = {
			isMe: true,
			limit: 100,
		};

		if (historyDateRange.start && historyDateRange.end) {
			const rangeStart = startOfLocalDay(
				parseLocalDateYmd(historyDateRange.start),
			);
			const rangeEnd = endOfLocalDay(
				parseLocalDateYmd(historyDateRange.end),
			);
			params.date_a = rangeStart.toISOString();
			params.date_b = rangeEnd.toISOString();
		}

		return params;
	}, [historyDateRange]);

	const {
		data: historySessionsData,
		isLoading: isHistorySessionsLoading,
		isError: isHistorySessionsError,
	} = useGetSessions(historySessionsQueryParams, {
		refetchInterval: PROFESSIONAL_HOME_SESSIONS_REFETCH_INTERVAL_MS,
	});

	const calendarSessions = calendarSessionsData?.data ?? [];
	const historyAllSessions = historySessionsData?.data ?? [];
	const hasHistorySessionsLoadError =
		isHistorySessionsError && historyAllSessions.length === 0;

	const historySessionsInDateRange = useMemo(
		() =>
			filterSessionsByHistoryDateRange(
				historyAllSessions,
				historyDateRange,
			),
		[historyAllSessions, historyDateRange],
	);

	const filteredHistorySessions = useMemo(() => {
		if (!historyStatusFilter) return historySessionsInDateRange;
		return historySessionsInDateRange.filter(
			(session) => session.status === historyStatusFilter,
		);
	}, [historySessionsInDateRange, historyStatusFilter]);

	useEffect(() => {
		if (view !== 'WEEK' || isCalendarSessionsLoading || isFetching) return;
		bumpWeekScrollAlignKey();
	}, [view, isCalendarSessionsLoading, isFetching, bumpWeekScrollAlignKey]);

	useEffect(() => {
		const session = getProfessionalHomeOpenSession(location.state);
		if (!session) return;

		let cancelled = false;

		const openSessionFromNavigation = async () => {
			await waitMs(PROFESSIONAL_BOOKING_STEP_DELAY_MS);
			if (cancelled) return;

			setCurrentDate(new Date(session.selected_date));
			setOpenedSession(session);
			setIsOpenedSessionModalVisible(true);
			navigate(
				{ pathname: location.pathname, search: location.search },
				{ replace: true, state: null },
			);
		};

		void openSessionFromNavigation();

		return () => {
			cancelled = true;
		};
	}, [location.pathname, location.search, location.state, navigate]);

	// Abre o prontuário do paciente quando vindo do fluxo de revisão pós-sessão.
	useEffect(() => {
		const openProntuario = getProfessionalHomeOpenProntuario(
			location.state,
		);
		if (!openProntuario) return;

		setOpenProntuarioConfig(openProntuario);
		navigate(
			{ pathname: location.pathname, search: location.search },
			{ replace: true, state: null },
		);
	}, [location.pathname, location.search, location.state, navigate]);

	const { data: openProntuarioSession } = useGetSession(
		openProntuarioConfig?.platformSessionId ?? '',
		{ enabled: !!openProntuarioConfig?.platformSessionId },
	);

	useEffect(() => {
		if (!canSchedulePatientSessions) return;

		const tutorialReturn = parseProfessionalTutorialHomeReturnState(
			location.state,
		);
		if (!tutorialReturn) return;

		setTutorialModalInitialId(tutorialReturn.tutorialId ?? null);
		setIsTutorialModalOpen(true);
		navigate(
			{ pathname: location.pathname, search: location.search },
			{ replace: true, state: null },
		);
	}, [
		canSchedulePatientSessions,
		location.pathname,
		location.search,
		location.state,
		navigate,
	]);

	const professionalGreetingName = professional
		? [(professional as any).name_prefix, professional.first_name]
				.filter(Boolean)
				.join(' ')
		: '';

	const buildAgendarParams = (extras?: Record<string, string>) => {
		if (!professional?.id) return null;

		const serviceId =
			professional.professional_services?.[0]?.service?.id ?? '';

		return encodeUrl({
			s: '1',
			professional_id: professional.id,
			service_id: serviceId,
			user_ids: '[]',
			...extras,
		});
	};

	const buildPrefillDateYmd = (date: Date) => {
		const y = date.getFullYear();
		const m = String(date.getMonth() + 1).padStart(2, '0');
		const d = String(date.getDate()).padStart(2, '0');
		return `${y}-${m}-${d}`;
	};

	const handleScheduleSlotClick = (date: Date, hour: number) => {
		const encodedParams = buildAgendarParams({
			prefill_date: buildPrefillDateYmd(date),
			prefill_hour: String(hour),
		});
		if (!encodedParams) return;
		navigate(`/profissional/consultas/agendar?d=${encodedParams}`);
	};

	return (
		<section className="flex h-full flex-col overflow-hidden bg-white">
			<div className="flex min-w-0 flex-1 flex-col overflow-hidden">
				<div className="relative min-h-0 flex-1">
					{isCalendarSessionsLoading ? (
						<div className="absolute inset-0 z-50 flex h-full w-full items-center justify-center bg-white/50">
							<div className="flex flex-col items-center gap-2">
								<div className="h-8 w-8 animate-spin rounded-full border-4 border-zinc-200 border-t-blue-600" />
								<span className="text-xs font-bold uppercase tracking-widest text-zinc-400">
									Carregando
								</span>
							</div>
						</div>
					) : view === 'MONTH' ? (
						<CalendarMonthView
							days={daysGrid as any}
							sessions={calendarSessions}
						/>
					) : (
						<CalendarWeekView
							days={daysGrid as Date[]}
							sessions={calendarSessions}
							alignNowKey={weekScrollAlignKey}
							onSlotClick={
								canSchedulePatientSessions
									? handleScheduleSlotClick
									: undefined
							}
						/>
					)}

					{professionalGreetingName &&
					!isProfessionalHomeGreetingDismissedForPageLoad() ? (
						<ProfessionalHomeGreetingNotification
							professionalGreetingName={professionalGreetingName}
							canSchedulePatientSessions={
								canSchedulePatientSessions
							}
							onOpenTutorial={() => setIsTutorialModalOpen(true)}
						/>
					) : null}
				</div>

				<ProfessionalHomeSummaryFooter
					sessions={calendarSessions}
					isLoading={isCalendarSessionsLoading}
				/>

				<ProfessionalSessionModal
					session={openedSession}
					isOpen={isOpenedSessionModalVisible}
					onClose={() => {
						setIsOpenedSessionModalVisible(false);
						setOpenedSession(null);
					}}
				/>

				<ProfessionalProntuarioModal
					isOpen={!!openProntuarioConfig}
					onClose={() => setOpenProntuarioConfig(null)}
					patientId={openProntuarioConfig?.patientId}
					session={openProntuarioSession ?? null}
					initialMedicalRecordSessionId={
						openProntuarioConfig?.medicalRecordSessionId ?? null
					}
					initialView={
						openProntuarioConfig?.medicalRecordSessionId
							? 'session-detail'
							: 'sessions-list'
					}
				/>

				{canSchedulePatientSessions ? (
					<ProfessionalTutorialsModal
						isOpen={isTutorialModalOpen}
						onClose={() => {
							setIsTutorialModalOpen(false);
							setTutorialModalInitialId(null);
						}}
						initialActiveTutorialId={tutorialModalInitialId}
					/>
				) : null}
			</div>

			<ProfessionalHistoryPanel
				sessions={filteredHistorySessions}
				allSessions={historySessionsInDateRange}
				isLoading={isHistorySessionsLoading}
				isError={hasHistorySessionsLoadError}
			/>
		</section>
	);
};

export { ProfessionalHome };
